import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useUserPreferences } from '../hooks/useUserPreferences';
import translationsEn from '../locales/en.json';
import translationsPt from '../locales/pt.json';

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

type Language = 'en' | 'pt';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => Promise<void>;
  t: (key: string, fallback?: string) => string;
  ts: (status: string) => string;
  translateError: (error: unknown) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const { preferences, updateLanguage } = useUserPreferences();
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    if (preferences?.language) {
      setLanguageState(preferences.language);
    }
  }, [preferences?.language]);

  const setLanguage = async (newLanguage: Language) => {
    await updateLanguage(newLanguage);
    setLanguageState(newLanguage);
  };

  const t = (key: string, fallback?: string): string => {
    const translations = language === 'pt' ? translationsPt : translationsEn;
    if (key in translations) {
      return (translations as Record<string, string>)[key];
    }
    return fallback || key;
  };

  const ts = (status: string): string => {
    const statusKey = `status.${status.toLowerCase().replace(/\s+/g, '')}`;
    const translations = language === 'pt' ? translationsPt : translationsEn;
    return (translations as Record<string, string>)[statusKey] || status;
  };

  const translateError = (error: unknown): string => {
    if (error instanceof ValidationError) {
      return error.message;
    }
    let message: string;
    if (error instanceof Error) {
      message = error.message;
    } else if (error && typeof error === 'object' && 'message' in error) {
      message = String((error as { message: unknown }).message);
    } else {
      message = String(error);
    }
    const lowerMsg = message.toLowerCase();

    // PostgREST PGRST204: the app is writing a column the database does not
    // have yet, i.e. a pending SQL update. Name the field so it is actionable
    // instead of surfacing "an unexpected error".
    const missingColumn = message.match(/could not find the '([^']+)' column/i);
    if (missingColumn || lowerMsg.includes('pgrst204') || lowerMsg.includes('schema cache')) {
      const column = missingColumn?.[1];
      return column
        ? t('error.missingColumn').replace('{0}', column)
        : t('error.missingColumnGeneric');
    }

    if (lowerMsg.includes('cannot coerce') || lowerMsg.includes('single json') || lowerMsg.includes('pgrst116')) {
      return t('error.dataConflict');
    }
    if (lowerMsg.includes('numeric field overflow') || lowerMsg.includes('out of range') || lowerMsg.includes('overflow')) {
      return t('error.numericOverflow');
    }
    if (lowerMsg.includes('foreign key') || lowerMsg.includes('violates foreign key')) {
      return t('error.foreignKeyViolation');
    }
    if (lowerMsg.includes('check constraint') || lowerMsg.includes('violates check')) {
      return t('error.checkConstraint');
    }
    if (lowerMsg.includes('not-null') || lowerMsg.includes('null value') || lowerMsg.includes('violates not-null')) {
      return t('error.nullConstraint');
    }
    if (lowerMsg.includes('row-level security') || lowerMsg.includes('policy') || lowerMsg.includes('rls')) {
      return t('error.policyViolation');
    }
    if (lowerMsg.includes('406') || lowerMsg.includes('not acceptable')) {
      return t('error.dataConflict');
    }
    if (lowerMsg.includes('400') || lowerMsg.includes('bad request')) {
      return t('error.badRequest');
    }
    if (lowerMsg.includes('network') || lowerMsg.includes('fetch') || lowerMsg.includes('failed to fetch')) {
      return t('error.networkError');
    }
    if (lowerMsg.includes('permission') || lowerMsg.includes('unauthorized') || lowerMsg.includes('403') || lowerMsg.includes('not allowed')) {
      return t('error.unauthorized');
    }
    if (lowerMsg.includes('not found') || lowerMsg.includes('404') || lowerMsg.includes('does not exist')) {
      return t('error.notFound');
    }
    if (lowerMsg.includes('500') || lowerMsg.includes('server') || lowerMsg.includes('internal')) {
      return t('error.serverError');
    }
    if (lowerMsg.includes('valid') || lowerMsg.includes('required') || lowerMsg.includes('invalid') || lowerMsg.includes('must be')) {
      return t('error.validationFailed');
    }
    if (lowerMsg.includes('campaigns_work_code_key') || lowerMsg.includes('idx_projecto_campaigns_work_code')) {
      return t('error.duplicateWorkCode');
    }
    if (lowerMsg.includes('duplicate') || lowerMsg.includes('already exists') || lowerMsg.includes('unique')) {
      return t('error.duplicateEntry');
    }
    if (lowerMsg.includes('timeout') || lowerMsg.includes('timed out')) {
      return t('error.timeout');
    }
    return t('error.generic');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, ts, translateError }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
